import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import { routes } from "../../configs/constants/routes";
import useQuery from "../../hooks/useQuery";
import { PASSWORD_REGEX } from "../../utils/ValidationRules";
import { toast } from "../../utils/toast";
import { useAuthStore } from "../../stores/auth.store";

type Inputs = {
	password: string;
	passwordConfirmation: string;
};

const validationSchema = yup.object().shape({
	password: yup.string().required("PASSWORD_REQUIRED").matches(PASSWORD_REGEX, {
		message: "PASSWORD_VALIDATION_MESSAGE",
		excludeEmptyString: true,
	}),
	passwordConfirmation: yup
		.string()
		.required("PASSWORD_REQUIRED")
		.oneOf([yup.ref("password")], "PASSWORD_MISMATCH"),
});

const ActivateAccountPage: React.FC = () => {
	const query = useQuery();
	const authStore = useAuthStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [activationFailed, setActivationFailed] = useState(false);
	const [success, setSuccess] = useState(false);

	const { register, handleSubmit, formState } = useForm<Inputs>({
		mode: "onBlur",
		resolver: yupResolver(validationSchema),
	});

	const navigateToLogin = () => {
		navigate(routes.LOGIN, { replace: true });
	};

	const onSubmit = handleSubmit(async ({ password }) => {
		const token = query.get("token");

		if (!token) {
			toast.error("Missing token");
		} else {
			const res = await authStore.activateAccount(password, token);
			setActivationFailed(!res);

			if (res) {
				setSuccess(true);
			}
		}
	});

	return (
		<Styles>
			{success ? (
				<SuccessContent>
					<AppText size="l" margin="0 0 2rem" textAlign="center">
						{t("activateAccount.success")}
					</AppText>
					<Button type="button" onClick={navigateToLogin}>
						{t("activateAccount.gotoLogin")}
					</Button>
				</SuccessContent>
			) : (
				<>
					<AppText
						margin="2rem"
						letterSpacing="0.24rem"
						color="grayText"
						size="l"
						textTransform="uppercase"
					>
						{t("activateAccount.title")}
					</AppText>
					<FormComponent onSubmit={onSubmit}>
						<AppInput
							{...register("password")}
							margin="0.5rem"
							placeholder="activateAccount.password"
							icon="password"
							type="password"
							errors={formState.errors?.password?.message}
						/>
						<AppInput
							{...register("passwordConfirmation")}
							margin="0.5rem"
							placeholder="activateAccount.passwordConfirmation"
							icon="password"
							type="password"
							errors={formState.errors?.passwordConfirmation?.message}
						/>
						{activationFailed && <AppText color="danger">{t("activateAccount.failed")}</AppText>}

						<Button margin="2rem">{t("activateAccount.activate")}</Button>
					</FormComponent>
				</>
			)}
		</Styles>
	);
};

export default ActivateAccountPage;

const FormComponent = styled.form`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 270px;
`;

const Styles = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: ${(props) => props.theme.colors.COLOR_DARK_GRAY};
	box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.1);
	border-radius: 5px;
	width: 25.625rem;
	height: 25.3125rem;
	justify-content: center;
	margin-bottom: 5vh; /* to push login box up slightly above vertical center */
`;

const SuccessContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 3rem;
`;
